import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_SKILL_PROJECTION, normalizeSkillProjection } from './skill-management-state.js';
import { createSettingsRequestOwner } from './settingsRequestOwner.js';

const emptyView = () => ({
  projection: EMPTY_SKILL_PROJECTION, loading: false, error: null, updatingSkillId: '',
});

export function createSkillRequestOwner({ skillAPI, instanceId, onChange }) {
  const owner = createSettingsRequestOwner({
    instanceId, targetType: 'skill', emptyProjection: EMPTY_SKILL_PROJECTION,
    normalizeProjection: normalizeSkillProjection,
    read: skillAPI?.getSkills?.bind(skillAPI), write: skillAPI?.updateSkill?.bind(skillAPI),
    onChange: ({ updatingId, ...view }) => onChange({ ...view, updatingSkillId: updatingId }),
  });
  return { ...owner, skillAPI };
}

export function useSkillManagement({ skillAPI, instanceId } = {}) {
  const [accepted, setAccepted] = useState({ instanceId, skillAPI, ...emptyView() });
  const ownerRef = useRef(null);
  useEffect(() => {
    const owner = createSkillRequestOwner({ skillAPI, instanceId,
      onChange: (view) => setAccepted({ instanceId, skillAPI, ...view }) });
    ownerRef.current = owner;
    setAccepted({ instanceId, skillAPI, ...emptyView(), loading: Boolean(skillAPI) });
    void owner.refresh().catch(() => {});
    return () => { owner.dispose(); if (ownerRef.current === owner) ownerRef.current = null; };
  }, [instanceId, skillAPI]);
  const refresh = useCallback((force = false) => (
    ownerRef.current?.instanceId === instanceId && ownerRef.current?.skillAPI === skillAPI
      ? ownerRef.current.refresh(force) : Promise.resolve(EMPTY_SKILL_PROJECTION)
  ), [instanceId, skillAPI]);
  const toggle = useCallback((item) => (
    ownerRef.current?.instanceId === instanceId && ownerRef.current?.skillAPI === skillAPI ? ownerRef.current.toggle(item) : Promise.resolve(null)
  ), [instanceId, skillAPI]);
  const view = accepted.instanceId === instanceId && accepted.skillAPI === skillAPI ? accepted : emptyView();
  return { ...view, refresh, toggle };
}

export default useSkillManagement;
